import { useEffect, useRef } from 'react';
import { DATA_ADAPTER } from '../config/adapters';
import { buildPlatformResultInboxFields } from '../core/notifications/platformResultMessages';

const RECENT_MS = 20 * 60 * 1000;

/**
 * In-app inbox for canonical platform draws (current turn).
 * Docs are written by Cloud Functions; this only listens.
 */
export function usePlatformResultBroadcast(opts: {
  lang: string;
  role: string;
  push: (fields: Record<string, unknown>, source?: string) => void;
}) {
  const { lang, role, push } = opts;
  const seenRef = useRef(new Set<string>());
  const startedAtRef = useRef(0);
  const pushRef = useRef(push);
  const langRef = useRef(lang);
  const roleRef = useRef(role);

  useEffect(() => {
    pushRef.current = push;
    langRef.current = lang;
    roleRef.current = role;
  });

  useEffect(() => {
    if (DATA_ADAPTER !== 'firebase') return undefined;
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    if (!startedAtRef.current) startedAtRef.current = Date.now();

    void import('../services/lottery/subscribePlatformResultBroadcasts').then(
      ({ subscribePlatformResultBroadcasts }) => {
        if (cancelled) return;
        unsubscribe = subscribePlatformResultBroadcasts((rows) => {
          for (const row of rows) {
            if (seenRef.current.has(row.id)) continue;
            if (!row.publishedAtMs) continue;
            if (row.publishedAtMs < startedAtRef.current - RECENT_MS) {
              seenRef.current.add(row.id);
              continue;
            }
            seenRef.current.add(row.id);
            pushRef.current(
              buildPlatformResultInboxFields(row, langRef.current, roleRef.current) as Record<string, unknown>,
              'push',
            );
          }
        });
      },
    );

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);
}
